/**
 * StockfishEngine React component (Workstream B, consumer-facing entry).
 *
 * Drives the SF18 typed UCI layer (engine.ts) + standard-UCI parser (parser.ts)
 * and re-emits results through the SAME `onResult` callback contract the old
 * custom-WASM component exposed, so every consumer is unchanged:
 *
 *   onResult(variationNumber, movesSAN, baseTurn, score, isMate, raw)
 *
 * What changed vs the old component:
 *   - Real `uci`/`uciok` + `isready`/`readyok` handshake before searching.
 *   - Standard UCI parsing; SAN derived client-side via chess.js; baseTurn
 *     derived from the FEN (no custom `baseTurn`/`pvSan`/`bmc` fields).
 *   - Search-token race handling: on FEN/settings change we stop -> await ack
 *     -> go, debounce rapid changes, and drop stale lines from prior searches.
 *   - Worker crash -> automatic reinit; unsupported browser -> graceful no-op.
 *
 * NOTE: `movesSAN` is now a CLEAN SAN array (no `pvSan`/`bmc` sentinel tokens).
 * Consumers that previously stripped those sentinels have been updated.
 */

import {useEffect, useRef} from 'react';
import {
  StockfishEngineClient,
  isEngineSupported,
  EngineInfoLine,
} from './engine';
import {parseInfoLine, baseTurnFromFen} from './parser';

export type OnResult = (
  variationNumber: number,
  movesSAN: string[],
  baseTurn: 'w' | 'b',
  score: string,
  isMate: boolean,
  raw: string,
) => void;

interface StockfishEngineProps {
  fen: string;
  depth?: number;
  variations?: number;
  engineOn?: boolean;
  onResult: OnResult;
}

const DEBOUNCE_MS = 150;

const StockfishEngine = ({
  fen,
  depth,
  variations,
  engineOn,
  onResult,
}: StockfishEngineProps) => {
  const clientRef = useRef<StockfishEngineClient | null>(null);
  const readyRef = useRef(false);
  // FEN that the engine's CURRENT search is analyzing — used so a parsed line's
  // SAN/baseTurn are computed against the right position.
  const activeFenRef = useRef<string>(fen);
  const onResultRef = useRef(onResult);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guard against reinit storms after a worker crash.
  const reinitInFlightRef = useRef(false);
  // Mirror the latest props into refs so startSearch (invoked from the
  // mount-once init effect and the crash-reinit path) never reads stale values.
  const propsRef = useRef({fen, depth, variations, engineOn});

  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    propsRef.current = {fen, depth, variations, engineOn};
  }, [fen, depth, variations, engineOn]);

  // ---- engine lifecycle (init / reinit on crash / destroy) ----
  useEffect(() => {
    if (!isEngineSupported() || !engineOn) {
      // Unsupported browser: graceful no-op (B6).
      return;
    }

    let cancelled = false;

    const handleInfo = (line: EngineInfoLine) => {
      const client = clientRef.current;
      if (!client) return;
      // Drop stale lines from a previous search/position (B4).
      if (line.token !== client.currentToken) return;
      const parsed = parseInfoLine(line.raw, activeFenRef.current);
      if (!parsed) return;
      onResultRef.current(
        parsed.variationNumber,
        parsed.movesSAN,
        parsed.baseTurn,
        parsed.score,
        parsed.isMate,
        line.raw,
      );
    };

    const handleError = () => {
      // Worker crash -> reinit once (B6).
      if (cancelled || reinitInFlightRef.current || !propsRef.current.engineOn)
        return;
      reinitInFlightRef.current = true;
      readyRef.current = false;
      try {
        clientRef.current?.destroy();
      } catch {
        /* noop */
      }
      clientRef.current = null;
      void initEngine();
    };

    const initEngine = async () => {
      const client = new StockfishEngineClient({
        onInfo: handleInfo,
        onError: handleError,
      });
      clientRef.current = client;
      try {
        await client.init();
        if (cancelled) {
          client.destroy();
          return;
        }
        readyRef.current = true;
        reinitInFlightRef.current = false;
        // If we should already be analyzing, kick it off post-(re)init using
        // the LATEST props (refs), not the stale values this effect closed over.
        if (propsRef.current.engineOn) {
          startSearch();
        }
      } catch {
        // Init failed (load failure / timeout / unsupported): no-op (B6).
        readyRef.current = false;
        reinitInFlightRef.current = false;
      }
    };

    void initEngine();

    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      clientRef.current?.destroy();
      clientRef.current = null;
      readyRef.current = false;
    };
  }, [engineOn]);

  // Issue a fresh search for the LATEST props (read from refs so this is safe
  // to call from the mount-once init/reinit path too). Stops any prior search
  // first (token bump discards its stale lines), then sets position + go.
  const startSearch = () => {
    const client = clientRef.current;
    if (!client || !readyRef.current) return;
    const {
      fen: curFen,
      depth: curDepth,
      variations: curVariations,
    } = propsRef.current;
    client.stop(); // bumps token; discards stale lines from the previous FEN
    client.newGame();
    activeFenRef.current = curFen;
    client.analyze(curFen, {
      depth: curDepth,
      multiPV: curVariations,
      movetime: 10000,
    });
  };

  // ---- react to FEN / depth / variations / engineOn changes (B4 debounce) ----
  useEffect(() => {
    if (!isEngineSupported()) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (engineOn) {
      debounceRef.current = setTimeout(() => {
        startSearch();
      }, DEBOUNCE_MS);
    } else {
      // Turn engine off: stop the current search (token bump drops stragglers).
      clientRef.current?.stop();
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [fen, depth, variations, engineOn]);

  return null;
};

export default StockfishEngine;

// Re-export for the rare consumer (and the shared engine page) that needs the
// pure helpers without the React wrapper.
export {parseInfoLine, baseTurnFromFen};
